// docs: https://momentjscom.readthedocs.io/en/latest/moment/04-displaying/01-format/
import {
    MAXIMUM_DELIVERY_DATE,
    MINIMUM_DELIVERY_DATE,
} from 'constants/estimations'
import { TIMEZONE } from 'constants/system'
import moment from 'moment'
import { capitalize } from './helpers'

// format: So.
export const getShortDayName = (date: string) =>
    capitalize(moment(date).format('ddd'))

// format: Sobota
export const getFullDayName = (date: string) =>
    capitalize(moment(date).format('dddd'))

// isShort=false format: 01.01.2022
// isShort=false && withDayName=true format: Czw. 01.01.2022
// isShort=true format: 01.01.22
// isShort=true && withDayName=true format: Czw. 01.01.22
export const getFormattedDate = ({
    date,
    withYear,
    isShortYear,
    withDayName,
}: {
    date: string
    withYear?: boolean
    isShortYear?: boolean
    withDayName?: boolean
}) => {
    let format = 'DD.MM.YYYY'
    if (isShortYear) {
        format = 'DD.MM.YY'
    }
    if (!withYear) {
        format = 'DD.MM'
    }
    if (withDayName) {
        format = `ddd ${format}`
    }
    return moment(date).format(format)
}

// format: 01.01. - 02.01.
// withYear=true format: 01.01.2022 - 02.01.2022
// withYear=true isShortYear=true format: 01.01.22 - 02.01.22
// TODO: parameters should be rather in object
export const formatDates = (
    dates: string[],
    withYear?: boolean,
    isShortYear?: boolean,
    withDayName?: boolean
) =>
    dates.length > 1
        ? `${getFormattedDate({
              date: dates[0],
              withYear,
              isShortYear,
              withDayName,
          })} - ${getFormattedDate({
              date: dates[dates.length - 1],
              withYear,
              isShortYear,
              withDayName,
          })}`
        : `${getFormattedDate({
              date: dates[0],
              withYear,
              isShortYear,
              withDayName,
          })}`

// format: So. 01.01.22 or  So. 01.01
export const getDayNameAndDate = ({
    date,
    isShortDayName = true,
    withYear = false,
}: {
    date: string
    isShortDayName?: boolean
    withYear?: boolean
}) =>
    `${
        isShortDayName ? getShortDayName(date) : getFullDayName(date)
    } ${getFormattedDate({
        date,
        withYear,
        isShortYear: true,
    })}`

// format: So. 01.01.22, 22:00
export const getDayNameAndDateWithTime = ({
    date,
    time,
    withYear = false,
}: {
    date: string
    time: string
    withYear?: boolean
}) => `${getDayNameAndDate({ date, withYear })}, ${time.replace(/^0/, '')}`

// format: 01.01, Czw.
export const getDateAndDayName = (date: string) =>
    `${getFormattedDate({
        date,
        withYear: false,
    })}, ${getShortDayName(date)}`

// format: 22.06.2022 (Śr.)
export const getDateAndDayNameBrackets = (date: string) =>
    `${getFormattedDate({ date, withYear: true })} (${getShortDayName(date)})`

// format date from 01.01.22 to 2022-01-01
export const formatDateISO = (date: string) => {
    const parts = date.split('.')

    return `${`20${parts[2]}`}-${parts[1]}-${parts[0]}`
}

/**
 * Returns an array of dates between two given dates (including them both)
 * @param {string} startDate - starting date in 'YYYY-MM-DD' format
 * @param {string} endDate - end date in 'YYYY-MM-DD' format
 */
export const getDatesBetweenDates = (startDate: string, endDate: string) => {
    const dates = []

    const currentDate = moment(startDate).startOf('day')
    const lastDate = moment(endDate).startOf('day')

    dates.push(currentDate.format('YYYY-MM-DD'))
    while (currentDate.add(1, 'days').diff(lastDate) <= 0) {
        dates.push(currentDate.format('YYYY-MM-DD'))
    }

    return dates
}

/**
 * Returns a number of days between two dates (including them both)
 * @param {string} startDate - starting date in 'YYYY-MM-DD' format
 * @param {string} endDate - end date in 'YYYY-MM-DD' format
 */
export const getNumberOfDaysBetweenDates = (
    startDate: string,
    endDate: string
) => {
    const start = moment(startDate, 'YYYY-MM-DD').startOf('day')
    const end = moment(endDate, 'YYYY-MM-DD').startOf('day')
    return end.diff(start, 'days') + 1
}

// return true/false
export const isStartDateAfterEndDate = (end: string): boolean => {
    const startDate = moment()
    const endDate = moment(end)

    return startDate.isAfter(endDate)
}

// format: 20.11 - 22.11
export const estimatedDeliveryDate = formatDates([
    moment().add(MINIMUM_DELIVERY_DATE, 'days').format('YYYY-MM-DD'),
    moment().add(MAXIMUM_DELIVERY_DATE, 'days').format('YYYY-MM-DD'),
])

export const isCurrentDateWithinRange = (
    startDate: string,
    endDate: string
) => {
    const startMoment = moment(startDate, 'YYYY-MM-DD')
        .startOf('day')
        .utcOffset(TIMEZONE)
    const endMoment = moment(endDate, 'YYYY-MM-DD')
        .endOf('day')
        .utcOffset(TIMEZONE)

    return moment().isBetween(startMoment, endMoment, undefined, '[]') // ===isBetweenOrEqual
}

export const isCurrentDateTimeWithinRange = (
    startDateTime: string,
    endDateTime: string
) => {
    const startPromotion = moment(startDateTime, 'YYYY-MM-DD HH:mm').utcOffset(
        TIMEZONE
    )
    const endPromotion = moment(endDateTime, 'YYYY-MM-DD HH:mm').utcOffset(
        TIMEZONE
    )

    return moment().isBetween(startPromotion, endPromotion)
}

export const isDateInRange = (
    dateToCheck: string,
    startDate: string,
    endDate: string
): boolean => {
    const date = new Date(dateToCheck)
    const start = new Date(startDate)
    const end = new Date(endDate)

    return date >= start && date <= end
}
